import { BulkSelectionState } from 'types/BulkActionTypes';
import { QueryParamType } from 'types/ListTypes';

/*
 * selectionUtils.ts
 * Helper functions for the include/exclude bulk selection state of lists,
 * shared by AbstractList, ListRow and the bulk actions panel.
 */

// Determines if a row is currently selected based on the selection mode
export function isRowSelected(selection: BulkSelectionState, id: number): boolean {
    return selection.mode === 'include' ? selection.ids.has(id) : !selection.ids.has(id);
}

// Toggles a single row in the selection, symmetric for both selection modes
export function toggleSelectionId(selection: BulkSelectionState, id: number): BulkSelectionState {
    // Copy the current ids so the state update stays immutable
    const newIds = new Set(selection.ids);

    // Add or remove the id, works the same for include and exclude mode
    if (newIds.has(id)) {
        newIds.delete(id);
    } else {
        newIds.add(id);
    }

    return { ...selection, ids: newIds };
}

// Toggles the header checkbox between selecting everything and selecting nothing
export function toggleSelectAllState(selection: BulkSelectionState): BulkSelectionState {
    // Everything is already selected when in exclude mode with no exceptions
    const allSelected = selection.mode === 'exclude' && selection.ids.size === 0;

    return allSelected ? { mode: 'include', ids: new Set() } : { mode: 'exclude', ids: new Set() };
}

// Derives the visible selected count from the selection state and the total matching rows
export function getSelectedCount(selection: BulkSelectionState, totalRows: number): number {
    return selection.mode === 'include' ? selection.ids.size : totalRows - selection.ids.size;
}

// Strips the pagination and ordering params, leaving only the active filters
export function buildBulkActionFilters(queryParams: QueryParamType): QueryParamType {
    const { page, page_size, ordering, ...filters } = queryParams;
    return filters;
}

// Builds the mode/ids part of the request body shared by every bulk action endpoint
export function buildBulkActionSelectionPayload(selection: BulkSelectionState): { mode: BulkSelectionState['mode']; ids: number[] } {
    return {
        mode: selection.mode,
        ids: Array.from(selection.ids),
    };
}
